/**
 * Form 컴포넌트 사용 이유
 * - 폼 전체를 구조적으로 관리하고, 유효성 검사(validation), 상태 관리, 에러 처리 등을 일관성 있게 처리하기 위함
 * - React Hook Form과 통합되어 구조화된 폼 구성과 유효성 검증 처리를 쉽게 만들어줌
 * 컴포넌트	역할 설명
  Form:	useForm()으로 생성된 인스턴스를 넘겨받아 context 제공
  FormField:	control, name을 기반으로 해당 필드 등록
  FormItem:	레이블 + 컨트롤 + 에러 메시지를 묶는 래퍼
  FormLabel:	해당 필드의 레이블
  FormControl:	실제 Input, Textarea 등 UI 컴포넌트를 감쌈
  FormMessage:	해당 필드의 에러 메시지를 출력
 */

import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import { Controller, FormProvider, useFormContext, useFormState, type ControllerProps, type FieldPath, type FieldValues } from "react-hook-form";

import { cn } from "@core/utils/domUtil";
import { Label } from "@core/components/ui/Label";

const Form = FormProvider;

type FormFieldContextValue<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({ ...props }: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

function FormContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="form-content" className={cn("form-content", className)} {...props} />;
}

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

function FormItem({ className, ...props }: React.ComponentProps<"div">) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div data-slot="form-item" className={cn("relative w-full", className)} {...props} />
    </FormItemContext.Provider>
  );
}

interface IFormLabelProps extends React.ComponentProps<typeof LabelPrimitive.Root> {
  className?: string;
  required?: boolean;
}
function FormLabel({ className, required, ...props }: IFormLabelProps) {
  const { error, formItemId } = useFormField();

  return <Label data-slot="form-label" required={required} data-error={!!error} className={cn("form-label data-[error=true]:text-error shrink-0", className)} htmlFor={formItemId} {...props} />;
}

function FormControl({ className, ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return <Slot data-slot="form-control" id={formItemId} aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`} aria-invalid={!!error} className={cn(className)} {...props} />;
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
  const { formDescriptionId } = useFormField();

  return <p data-slot="form-description" id={formDescriptionId} className={cn("form-description", className)} {...props} />;
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : props.children;

  if (!body) {
    return null;
  }

  return (
    <p data-slot="form-message" id={formMessageId} className={cn("form-message text-error", className)} {...props}>
      {body}
    </p>
  );
}

export { useFormField, Form, FormContent, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField };
